import { useCallback, useEffect, useRef, useState } from "react";

import { useRouteMutation } from "@web-app/api";
import { useAlert } from "@web-app/components";
import useDispatchStore from "@web-app/store/dispatchStore";
import { ComputedRouteData, Order } from "@web-app/types";

const POLYLINE_COLOR = "#6b7280";

export function useRouteDrawing(
  editRouteOrderIds: string[],
  orders: Map<string, Order> | undefined,
  currentUser: { currentWorkspaceId: string } | null,
  assigneeId: string | null,
  name: string,
  isFlexible: boolean,
  respectTimeWindows: boolean,
  useEarlyThreshold: boolean,
  earlyThresholdMinutes: number,
  estimatedPickupTime?: Date | null,
) {
  const [distance, setDistance] = useState<number>(0);
  const [durations, setDurations] = useState<number[]>([]);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const { createRouteDraft } = useRouteMutation();
  const { directionsRenderer, dispatchMapRef } = useDispatchStore();
  const { showAlert } = useAlert();
  const [routedOrdersDraftEstimatedETA, setRoutedOrdersDraftEstimatedETA] =
    useState<Map<string, Date | null>>(new Map());
  const currentPolylineRef = useRef<google.maps.Polyline | null>(null);
  const [computedRouteData, setComputedRouteData] =
    useState<ComputedRouteData | null>(null);

  const orderIdsKey = editRouteOrderIds.join(",");
  const pickupTimeKey = estimatedPickupTime?.toISOString() ?? "";

  useEffect(() => {
    setComputedRouteData(null);
  }, [orderIdsKey, pickupTimeKey]);

  const drawSimpleRoute = useCallback(
    (editRouteOrderIds: string[], shouldFitBounds = true) => {
      if (!dispatchMapRef?.current) {
        return;
      }

      // Remove previous polyline if it exists
      if (currentPolylineRef.current) {
        currentPolylineRef.current.setMap(null);
        currentPolylineRef.current = null;
      }

      // Get all valid orders from editRouteOrderIds
      const validOrders = editRouteOrderIds
        .map((id) => orders?.get(id))
        .filter((order): order is Order => !!order);

      if (validOrders.length > 0) {
        try {
          // Create a simple path connecting all order locations
          const path = validOrders.map((order) => {
            if (order.type === "ROUTE_OPT_PICKUP") {
              return {
                lat: order.pickupLocation.coordinates[1],
                lng: order.pickupLocation.coordinates[0],
              };
            } else {
              return {
                lat: order.dropoffLocation.coordinates[1],
                lng: order.dropoffLocation.coordinates[0],
              };
            }
          });

          // Create and render the polyline
          currentPolylineRef.current = new google.maps.Polyline({
            geodesic: true,
            icons: [
              {
                icon: {
                  path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                },
                offset: "0",
                repeat: "100px",
              },
            ],
            map: dispatchMapRef.current,
            path: path,
            strokeColor: POLYLINE_COLOR,
            strokeOpacity: 1.0,
            strokeWeight: 2,
          });

          setDurations([]);
          setDistance(0);

          // Only fit bounds if requested (not during rectangle selection) and not in edit mode
          if (shouldFitBounds && path.length > 0) {
            const { routeModalMode } = useDispatchStore.getState();
            if (routeModalMode !== "edit") {
              const bounds = new google.maps.LatLngBounds();
              path.forEach((point) => bounds.extend(point));
              dispatchMapRef.current.fitBounds(bounds);
            }
          }
        } catch {
          // If there's an error creating the polyline, clear any existing one
          if (currentPolylineRef.current) {
            currentPolylineRef.current.setMap(null);
            currentPolylineRef.current = null;
          }
        }
      }
    },
    [orders, dispatchMapRef],
  );

  const drawDirections = useCallback(
    (shouldOptimize = false) => {
      if (directionsRenderer && dispatchMapRef?.current) {
        // Clear any existing routes
        directionsRenderer.setMap(null);

        // Remove previous polyline if it exists
        if (currentPolylineRef.current) {
          currentPolylineRef.current.setMap(null);
          currentPolylineRef.current = null;
        }

        // Get all valid orders from editRouteOrderIds
        const validOrders = editRouteOrderIds
          .map((id) => orders?.get(id))
          .filter((order): order is Order => !!order);

        if (validOrders.length > 0) {
          showAlert({
            id: "directions-loading",
            message: shouldOptimize
              ? "Calculating the optimal route for your orders"
              : "Calculating the route for your orders",
            title: shouldOptimize
              ? "Optimizing Route"
              : "Generating Route Preview",
            type: "loading",
          });

          // Create route draft using the mutation
          createRouteDraft.mutate(
            {
              assigneeId: assigneeId || undefined,
              currentWorkspaceId: currentUser!.currentWorkspaceId,
              earlyThresholdMinutes,
              estimatedPickupTime: estimatedPickupTime
                ? estimatedPickupTime.toISOString()
                : null,
              externalId: name,
              isFlexible: isFlexible,
              orderIds: validOrders.map((order) => order.id),
              respectTimeWindows,
              shouldOptimize,
              useEarlyThreshold,
            },
            {
              onError: (error) => {
                console.error("Route draft creation failed:", error);
                showAlert({
                  id: "directions-loading",
                  message: shouldOptimize
                    ? "Failed to optimize the route"
                    : "Failed to calculate the route for your orders",
                  title: shouldOptimize
                    ? "Route Optimization Failed"
                    : "Route Preview Failed",
                  type: "error",
                });
              },
              onSuccess: (data) => {
                if (data.route?.routePolyline?.routes?.[0]) {
                  setRoutedOrdersDraftEstimatedETA(new Map());

                  const route = data.route.routePolyline.routes[0];

                  // Create and render the polyline
                  const path = window.google.maps.geometry.encoding.decodePath(
                    route.polyline.encodedPolyline,
                  );

                  currentPolylineRef.current = new google.maps.Polyline({
                    geodesic: true,
                    icons: [
                      {
                        icon: {
                          path: window.google.maps.SymbolPath
                            .FORWARD_CLOSED_ARROW,
                        },
                        offset: "0",
                        repeat: "100px",
                      },
                    ],
                    map: dispatchMapRef.current,
                    path: path,
                    strokeColor: POLYLINE_COLOR,
                    strokeOpacity: 1.0,
                    strokeWeight: 2,
                  });

                  // Calculate total distance and durations
                  const totalDistance = route.distanceMeters || 0;
                  const distanceInMiles = totalDistance / 1609.34;

                  // Parse duration string to seconds (assuming format like "1h 30m")
                  const parseDuration = (durationStr: string): number => {
                    const hours = durationStr.match(/(\d+)h/)?.[1] || "0";
                    const minutes = durationStr.match(/(\d+)m/)?.[1] || "0";
                    return parseInt(hours) * 3600 + parseInt(minutes) * 60;
                  };

                  const durations = route.legs.map((leg) =>
                    parseDuration(leg.duration),
                  );

                  setDurations(durations);
                  setDistance(distanceInMiles);

                  // If this was an optimization request, update the order sequence
                  if (shouldOptimize && data.route.ordersOrder) {
                    const optimizedOrderIds = data.route.ordersOrder
                      .sort((a, b) => a.index - b.index)
                      .map((order) => order.id);

                    // Only update if the order actually changed
                    const hasOrderChanged =
                      optimizedOrderIds.length !== editRouteOrderIds.length ||
                      optimizedOrderIds.some(
                        (id, index) => id !== editRouteOrderIds[index],
                      );

                    if (hasOrderChanged) {
                      useDispatchStore.setState({
                        editRouteOrderIds: optimizedOrderIds,
                      });
                    }
                  }

                  data.routeOrders.forEach((order) => {
                    setRoutedOrdersDraftEstimatedETA((prev) => {
                      const newMap = new Map(prev);
                      newMap.set(order.id, order.estimatedEtaDateTime);
                      return newMap;
                    });
                  });

                  setComputedRouteData({
                    orderETAs: data.routeOrders.map((order) => ({
                      estimatedEtaDateTime: order.estimatedEtaDateTime
                        ? String(order.estimatedEtaDateTime)
                        : null,
                      orderId: order.id,
                    })),
                    routePolyline: data.route.routePolyline,
                  });

                  showAlert({
                    id: "directions-loading",
                    message: shouldOptimize
                      ? "The optimal route has been calculated"
                      : "The route for your orders has been calculated",
                    title: shouldOptimize
                      ? "Route Optimized"
                      : "Route Preview Generated",
                    type: "success",
                  });
                }
              },
            },
          );
        }
      }
    },
    [
      directionsRenderer,
      orders,
      editRouteOrderIds.length,
      createRouteDraft,
      currentUser,
      assigneeId,
      name,
      isFlexible,
      respectTimeWindows,
      useEarlyThreshold,
      earlyThresholdMinutes,
      estimatedPickupTime,
    ],
  );

  const optimizeOrders = async () => {
    setIsOptimizing(true);
    try {
      await drawDirections(true);
    } catch (err) {
      console.error("Failed to optimize orders:", err);
    } finally {
      setIsOptimizing(false);
    }
  };

  return {
    computedRouteData,
    currentPolylineRef,
    distance,
    drawDirections,
    drawSimpleRoute,
    durations,
    isOptimizing,
    optimizeOrders,
    routedOrdersDraftEstimatedETA,
    setRoutedOrdersDraftEstimatedETA,
  };
}
